'use client'

import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  getCoreRowModel,
  PaginationState,
  TableOptions,
  Updater,
  useReactTable,
} from '@tanstack/react-table'

import { useDebounce } from '@/lib/hooks/use-debounce'

import { useDebouncedCallback } from '@/hooks/use-debounced-callback'

export type Pagination = {
  page: number
  limit: number
}

type DataTableFilteringState<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  [K in keyof T]: T[K]
}

export function useDataTableState() {
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 10,
  })

  const [search, setSearch] = useState('')

  const paginationDebounced = useDebounce(pagination, 400)

  return { pagination, setPagination, paginationDebounced, search, setSearch }
}

type UseDataTableProps<TData, TFilter extends DataTableFilteringState> = Omit<
  TableOptions<TData>,
  'getCoreRowModel'
> & {
  isLoading?: boolean
  rowCount?: number
  pagination: {
    state: Pagination
    onChange: Dispatch<SetStateAction<Pagination>>
  }
  search?: {
    state: string
    onChange: (value: string) => void
    debounce?: number
  }
  filters?: {
    state: TFilter
    onChange: Dispatch<SetStateAction<TFilter>>
  }
}

export type UseDataTableReturn<
  TData = any,
  TFilter extends DataTableFilteringState = any,
> = ReturnType<typeof useDataTable<TData, TFilter>>

export function useDataTable<TData, TFilter extends DataTableFilteringState>({
  pagination,
  search,
  filters,
  rowCount = 10,
  isLoading = false,
  ...tableProps
}: UseDataTableProps<TData, TFilter>) {
  const tablePagination = useMemo(
    (): PaginationState => ({
      pageIndex: pagination.state.page - 1, // Tanstack table is zero-based
      pageSize: pagination.state.limit,
    }),
    [pagination.state]
  )

  function onPaginationChange(updaterOrValue: Updater<PaginationState>) {
    const value =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(tablePagination ?? { pageIndex: 0, pageSize: 10 })
        : updaterOrValue

    const next = {
      page: value.pageIndex + 1,
      limit: value.pageSize,
    }

    pagination.onChange(next)
  }

  const pageCount = useMemo(() => {
    if (rowCount === 0) return 0
    return Math.ceil(rowCount / tablePagination.pageSize)
  }, [rowCount, tablePagination.pageSize])

  const table = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination: tablePagination,
      ...tableProps.state,
    },
    onPaginationChange,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    pageCount,
    ...tableProps,
  })

  const resetPagination = useCallback(() => {
    table.setPageIndex(0)
  }, [table])

  const setFilter = useCallback(
    function handleSetFilter<Key extends keyof TFilter>(
      id: Key,
      value: TFilter[Key]
    ) {
      if (!filters?.onChange) return
      filters.onChange((x) => ({ ...x, [id]: value }))
      resetPagination()
    },
    [filters, resetPagination]
  )

  const {
    state: searchState,
    onChange: onSearchChange,
    debounce = 300,
  } = search || {}
  const [localSearch, setLocalSearch] = useState(searchState ?? '')

  useEffect(() => {
    setLocalSearch(searchState ?? '')
  }, [searchState])

  const getSearch = useCallback(() => {
    return localSearch
  }, [localSearch])

  const debouncedSearchChange = useDebouncedCallback((s: string) => {
    if (!tableProps.manualFiltering) {
      table.setGlobalFilter(s)
    }

    onSearchChange?.(s)
    resetPagination()
  }, debounce)

  const onSearchChangeHandler = useCallback(
    (search: string) => {
      setLocalSearch(search)
      debouncedSearchChange?.(search)
    },
    [debouncedSearchChange]
  )

  const resetFilters = useCallback(() => {
    filters?.onChange(
      Object.keys(filters.state).reduce((acc, key) => {
        // @ts-expect-error
        acc[key] = null
        return acc
      }, {} as TFilter)
    )
    onSearchChangeHandler('')
  }, [filters, onSearchChangeHandler])

  return {
    table,
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    filters: filters?.state!,
    setFilter,
    onSearchChange: onSearchChangeHandler,
    getSearch,
    resetFilters,
    rowCount,
    isLoading,
  }
}

export type UseDataTableQueryProps<
  TData,
  TFilter extends DataTableFilteringState,
> = Omit<
  UseDataTableProps<TData, TFilter>,
  'data' | 'isLoading' | 'rowCount' | 'pageCount'
> & {
  query: {
    isLoading: boolean
    data:
      | {
          data: TData[]
          pagination: {
            page: number
            limit: number
            total_items: number
            total_pages: number
          }
        }
      | undefined
  }
}

export function useDataTableQuery<
  TData,
  TFilter extends DataTableFilteringState,
>({ query, ...tableProps }: UseDataTableQueryProps<TData, TFilter>) {
  return useDataTable({
    data: query.data?.data ?? [],
    isLoading: query.isLoading,
    pageCount: query.data?.pagination.total_pages,
    rowCount: query.data?.pagination.total_items,
    ...tableProps,
  })
}

/**
 * useReactTable
 * - useDataTable
 *    - useDataTableQuery
 */
